import React from 'react';
import not from 'ramda/src/not';
import isNil from 'ramda/src/isNil';
import { ConditionArticlePageContainer, ConditionArticlePageContainerValue } from '../../../containers/ConditionArticlePageContainer';
import { Helmet } from '../../../components/Helmet';
import { BranchScript } from '../../../components/BranchScript';
import { PreloadImage } from '../../../components/PreloadImage';
import { getArticleBySlugResponseTransformer, healthArticleToHealthArticlePageTransformer } from '../../../transformers';
import { BASE_URL, HealthArticleType, Routes, WagContentApiEndpoint, BRANCH_KEY, AnimalTypeSlug } from '../../../constants';
import { DEFAULT_CACHE_POLICY } from '../../../constants/cache';
import { HttpHeaderKey } from '../../../constants/http-header-key';
import { axiosContent, breadcrumbsBuilder, getPrevNextUrl } from '../../../utils';
import { requestHandler } from '../../../utils/requestHandler';
import { extractHref } from '../../../utils/extractHref';
import { HealthArticleResponse } from '../../../types/api';
type PageMeta = {
  metaTitle: string;
  metaDesc: string;
};
type Props = {
  value: ConditionArticlePageContainerValue;
  meta: PageMeta;
  slug: string;
  hasNextPage: boolean;
  nextPageUrl: null | string;
  prevPageUrl: null | string;
  href: string;
  vetAdviceUrl: string;
  animalTypeSlug: AnimalTypeSlug;
};
const ConditionDirectories = {
  cat: {
    title: 'Cat Conditions',
    url: Routes.CatConditionArticleDirectory
  },
  horse: {
    title: 'Horse Conditions',
    url: Routes.HorseConditionArticleDirectory
  },
  reptile: {
    title: 'Lizard Conditions',
    url: Routes.ReptileConditionArticleDirectory
  },
  bird: {
    title: 'Bird Conditions',
    url: Routes.BirdConditionArticleDirectory
  },
  rabbit: {
    title: 'Rabbit Conditions',
    url: Routes.RabbitConditionArticleDirectory
  },
  chinchilla: {
    title: 'Chinchilla Conditions',
    url: Routes.ChinchillaConditionArticleDirectory
  },
  ferret: {
    title: 'Ferret Conditions',
    url: Routes.FerretConditionArticleDirectory
  }
};
const ConditionArticlePage = ({
  value,
  meta: {
    metaDesc,
    metaTitle
  },
  slug,
  hasNextPage,
  nextPageUrl,
  prevPageUrl,
  href,
  vetAdviceUrl,
  animalTypeSlug
}: Props) => <>
    <Helmet title={metaTitle} description={metaDesc} canonical={href} robots="index, follow" twitter={{
    title: metaTitle,
    description: metaDesc,
    image: value.articlePhotoSrc
  }} openGraph={{
    title: metaTitle,
    description: metaDesc,
    image: value.articlePhotoSrc
  }} prevPageUrl={prevPageUrl} nextPageUrl={nextPageUrl} data-sentry-element="Helmet" data-sentry-source-file="[animal-health-article-slug].tsx">
      <BranchScript branchKey={BRANCH_KEY} data-sentry-element="BranchScript" data-sentry-source-file="[animal-health-article-slug].tsx" />
    </Helmet>

    <PreloadImage src={value.articlePhotoSrc} data-sentry-element="PreloadImage" data-sentry-source-file="[animal-health-article-slug].tsx" />

    <ConditionArticlePageContainer value={value} metaTitle={metaTitle} nextPageUrl={nextPageUrl} slug={slug} hasNextPage={hasNextPage} vetAdviceUrl={vetAdviceUrl} animalTypeSlug={animalTypeSlug} data-sentry-element="ConditionArticlePageContainer" data-sentry-source-file="[animal-health-article-slug].tsx" />
  </>;
const handleGetServerSideProps = requestHandler({
  headers: {
    [HttpHeaderKey.CacheControl]: DEFAULT_CACHE_POLICY
  }
});
export const getServerSideProps = handleGetServerSideProps(async ({
  req,
  query
}) => {
  const {
    slug: animalFromQuery,
    'animal-health-article-slug': animalHealthArticleSlugFromQuery,
    page = 1
  } = query;
  const animal = String(animalFromQuery);
  const animalHealthArticleSlug = String(animalHealthArticleSlugFromQuery);
  const url = WagContentApiEndpoint.GetAnimalConditionArticleBySlug.replace('{{animal}}', animal).replace('{{slug}}', animalHealthArticleSlug);
  const vetAdviceUrl = WagContentApiEndpoint.GetConditionArticleVetAdvice.replace('{{animal}}', animal).replace('{{slug}}', animalHealthArticleSlug);
  const response = await axiosContent.get<HealthArticleResponse>(url, {
    params: {
      page
    },
    // To support 301/302 http statuses, do not follow redirects from responses
    maxRedirects: 0
  });
  const transformedData = getArticleBySlugResponseTransformer(response.data);
  const componentData = healthArticleToHealthArticlePageTransformer({
    ...transformedData,
    type: HealthArticleType.Condition
  });
  const {
    nextPage,
    prevPage
  } = transformedData;
  const hasNextPage = not(isNil(nextPage));
  const {
    metaTitle,
    metaDesc,
    title,
    ...rest
  } = componentData;
  const href = extractHref(req, BASE_URL, prevPage, page);
  const {
    prevPageUrl,
    nextPageUrl
  } = getPrevNextUrl(prevPage, nextPage, href);
  const breadcrumbItems = breadcrumbsBuilder().add({
    title: ConditionDirectories[animal].title,
    url: ConditionDirectories[animal].url
  }).add({
    title
  }).build();
  return {
    props: {
      value: {
        title,
        breadcrumbItems,
        ...rest
      },
      meta: {
        metaTitle,
        metaDesc
      },
      href,
      slug: animalHealthArticleSlug,
      hasNextPage,
      nextPageUrl,
      prevPageUrl,
      vetAdviceUrl,
      animalTypeSlug: animal
    }
  };
});
export default ConditionArticlePage;